import { Editor } from '@tinymce/tinymce-react';
import AppContext from 'App/AppContext';
import { IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { AcceptType, allowedWebImageMimeTypes } from 'globalConstants';
import { globalOutcomeListCss } from 'globalStyles';
import { StoreMgmtStates } from 'models/adminTenant';
import { forwardRef, useContext, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createISOControlReference,
  getISOControlReferenceUrl,
  parseISOControlReference,
} from 'services/Api/isoControlService';
import { Editor as TinyMCEEditor } from 'tinymce';
import AppError from 'utils/appError';
import ISOControlReferencePicker from './ISOControlReferencePicker';
import './RichTextEditor.css';

export interface IRichTextEditorMCE {
  focus: () => void;
  getContent: () => string;
  // replaces the whole content as one undo level, so Ctrl+Z restores the previous text
  setContent: (html: string) => void;
  insertContent: (html: string) => void;
}

export interface IRichTextEditorMCEProps {
  className?: string;
  html?: string;
  placeholder?: string;
  height?: string | number;
  maxHeight?: number | undefined;
  minHeight?: number;
  maxLength?: number | undefined;
  readOnly?: boolean;
  disallowFullscreen?: boolean;
  compactToolbar?: boolean;
  hideRemainingChars?: boolean;
  toolbarOnTop?: boolean;
  stickyToolbar?: boolean;
  disableAutoFocus?: boolean;
  // Cleans what AI tools leave in pasted content (markdown symbols, invisible characters, citation
  // markers, inline styles) and tells the user once per paste. Off by default so other editors are
  // unaffected.
  cleanAiPaste?: boolean;
  onChange: (value: string) => void;
  onBadgeClick?: (badgeText: string) => void;
  // Fired when the editor loses focus. hasChanged is false when the content is byte-identical to what TinyMCE
  // produced when it loaded, so a caller that autosaves can skip a blur that did not change anything.
  onBlur?: (lastValue: string | undefined, hasChanged: boolean) => void;
  onFocus?: () => void;
  // Fired when the text selection changes, with the currently selected plain text ('' when nothing is
  // selected). Used to surface the selection to the Copilot chat; safe to leave undefined.
  onSelectionChange?: (selectedText: string) => void;
  onKeyUp?: (event: Partial<React.KeyboardEvent>) => void;
  onKeyDown?: (event: Partial<React.KeyboardEvent>) => void;
  onAccept?: (value: string | undefined, accept: boolean) => void;
  // When set, the editor offers the image plugin and uploads every inserted, pasted or dropped image through
  // this callback, which returns the URL the image is served from. Without it, the editor has no images.
  onImageUpload?: (file: Blob, fileName: string) => Promise<string>;
  // The image types the editor accepts with onImageUpload; defaults to allowedWebImageMimeTypes.
  acceptedImageTypes?: AcceptType;
  // Called when the pasted content is a list of 2+ lines (bulleted, numbered or one step per line),
  // with the items as plain text and the pasted content itself. The editor then does not insert the
  // paste: the caller decides what to do with it. Leave undefined for a normal paste.
  onPasteList?: (items: string[], pastedContent: string) => void;
  // Fired once TinyMCE is ready, after its own autofocus; the editor above uses it to place the caret
  // when it mounted because an AI proposal landed in the field.
  onInit?: () => void;
  // Called in TinyMCE's setup of each editor this component creates, before the editor renders: a caller
  // registers its own options and event handlers here without changing the other editors of the app.
  onSetup?: (editor: TinyMCEEditor) => void;
}

// TinyMCE takes the accepted image types as file extensions: the MIME subtype plus any extensions listed.
const getImageFileTypes = (types: AcceptType): string => {
  const extensions = Object.entries(types).flatMap(([mimeType, typeExtensions]) => [
    mimeType.split('/')[1],
    ...typeExtensions.map((extension) => extension.replace(/^\./, '')),
  ]);

  return Array.from(new Set(extensions)).join(',');
};

// An anchor inside an anchor is invalid HTML. TinyMCE repairs it by wrapping the inner one in a div,
// which the browser parser then uses to close the paragraph: the block splits and the outer link is
// lost. Put the caret after the enclosing link instead of inside it.
const moveCaretOutOfLink = (editor: TinyMCEEditor) => {
  const enclosing = editor.dom.getParent(editor.selection.getNode(), 'a');
  const parent = enclosing?.parentNode;
  if (!enclosing || !parent) return;

  editor.selection.setCursorLocation(parent, Array.prototype.indexOf.call(parent.childNodes, enclosing) + 1);
};

// While an image upload is pending, TinyMCE holds the image as a base64 data: or blob: source. That source is
// replaced by a short URL once the upload is done, so it is left out of the length that the limit counts.
const pendingImageSourcePattern = /(src\s*=\s*["'])(?:data|blob):[^"']*/gi;

const RichTextEditorMCE = forwardRef(function RichTextEditorMCE(props: IRichTextEditorMCEProps, ref) {
  const { t } = useTranslation(['translation']);
  const appContext = useContext(AppContext);
  const [currentLength, setCurrentLength] = useState<number>(props.html?.length ?? 0);
  const [reference, setReference] = useState<{ id?: number; label?: string }>();
  const referenceOpen = useRef(false);
  const referenceBookmark = useRef<ReturnType<TinyMCEEditor['selection']['getBookmark']>>();
  const referenceAnchor = useRef<HTMLAnchorElement>();
  const maxLength = props.maxLength ?? 4000;
  //only an editor that uploads its images holds them as pending sources; the others count the content as it is
  const getLength = (content: string): number =>
    props.onImageUpload ? content.replace(pendingImageSourcePattern, '$1').length : content.length;
  // Packaging tenants only (Creator, Manager, or a released Creator = Live); never a Customer tenant.
  const canAuthorReferences = appContext.user.login.storeMgmtState !== StoreMgmtStates.Customer;
  const editorRef = useRef<TinyMCEEditor | null>(null);
  //the upload handler calls the callback of the latest render (callback ref, as `useKeyPress`)
  const onImageUploadRef = useRef(props.onImageUpload);
  useLayoutEffect(() => {
    onImageUploadRef.current = props.onImageUpload;
  });
  //the TinyMCE init (and with it paste_preprocess) is created once, so it reads the latest callback
  //from here
  const onPasteListRef = useRef(props.onPasteList);
  useLayoutEffect(() => {
    onPasteListRef.current = props.onPasteList;
  });
  // The last content the caller accepted: the html prop, or an onEditorChange value within maxLength.
  // The blur handler falls back to it when the editor holds over-limit content. It must hold TinyMCE's
  // serialization form (see initialContentRef below), so the raw prop only stands in until the editor
  // exists; init_instance_callback and the props.html effect re-seed it from getContent().
  const lastAcceptedContentRef = useRef<string | undefined>(props.html);
  // The paste cleaner pulls in react-dom/server, so it is a lazy chunk that only an editor that cleans pastes
  // loads. paste_preprocess is synchronous: until the module has arrived a paste is a normal paste.
  const pasteCleanerRef = useRef<typeof import('utils/aiPasteCleaner')>();
  const cleansPastes = !!(props.cleanAiPaste || props.onPasteList);
  useEffect(() => {
    if (!cleansPastes) return;

    void import('utils/aiPasteCleaner').then((module) => {
      pasteCleanerRef.current = module;
    });
  }, [cleansPastes]);
  // TinyMCE re-serializes the HTML it is given when it loads it (forced root blocks, entity encoding,
  // attribute and style normalization, newlines between block elements), so getContent() can differ from the
  // html prop even when the user typed nothing. Remember what TinyMCE itself produced at load time and compare
  // the blur value against that, so callers can tell a real edit from a pure re-serialization.
  const initialContentRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Content pushed through the prop counts as accepted. The Editor child applies the prop in its
    // componentDidUpdate, which runs before this effect, so getContent() here is TinyMCE's serialization
    // of the new prop -- the same form as initialContentRef. Before init the raw prop stands in.
    lastAcceptedContentRef.current = editorRef.current ? editorRef.current.getContent() : props.html;
  }, [props.html]);

  const showMaxLengthExceededMsg = () => {
    appContext.showNotification(t('translation:General.Text.MaxLengthExceeded', { max: maxLength }), true);
  };

  // The one path from editor content to the caller, with the length guard.
  const emitChange = (value: string) => {
    if (getLength(value) <= maxLength) {
      lastAcceptedContentRef.current = value;
      props.onChange(value);
    } else {
      showMaxLengthExceededMsg();
    }
    setCurrentLength(getLength(value));
  };

  // No dependency list: the handle calls the current props, not the ones of the first render.
  useImperativeHandle(ref, (): IRichTextEditorMCE => {
    return {
      focus() {
        editorRef.current?.focus();
      },
      getContent() {
        return editorRef.current?.getContent() ?? '';
      },
      setContent(html: string) {
        const editor = editorRef.current;
        if (!editor) return;

        editor.undoManager.transact(() => editor.setContent(html));
        emitChange(editor.getContent());
      },
      insertContent(html: string) {
        const editor = editorRef.current;
        if (!editor) return;

        editor.insertContent(html);
        emitChange(editor.getContent());
      },
    };
  });

  const openReferencePicker = (editor: TinyMCEEditor) => {
    if (!canAuthorReferences) return;
    const selected = editor.dom.getParent(editor.selection.getNode(), 'a') as HTMLAnchorElement | null;
    const id = parseISOControlReference(selected?.getAttribute('href'));
    referenceAnchor.current = id && selected ? selected : undefined;
    referenceBookmark.current = editor.selection.getBookmark(2, true);
    referenceOpen.current = true;
    setReference({ id, label: id ? selected?.textContent ?? '' : editor.selection.getContent({ format: 'text' }) });
  };
  const closeReferencePicker = () => {
    setReference(undefined);
    referenceOpen.current = false;
    editorRef.current?.focus();
  };
  const insertReference = (id: number, label: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    const html = createISOControlReference(id, label);
    editor.focus();
    if (referenceBookmark.current) editor.selection.moveToBookmark(referenceBookmark.current);
    if (referenceAnchor.current && editor.getBody().contains(referenceAnchor.current)) {
      editor.selection.select(referenceAnchor.current);
    } else if (editor.selection.isCollapsed()) {
      moveCaretOutOfLink(editor);
    } else {
      // A selection that starts or ends inside a link replaces that whole link: an anchor cannot hold an anchor.
      const enclosing =
        editor.dom.getParent(editor.selection.getStart(), 'a') ?? editor.dom.getParent(editor.selection.getEnd(), 'a');
      if (enclosing) editor.selection.select(enclosing);
    }

    // The selection is exactly what insertContent replaces, so TinyMCE's own serialization of it is
    // the length the new anchor has to pay for.
    const replaced = editor.selection.getContent({ format: 'html' });
    if (getLength(editor.getContent()) - replaced.length + html.length > maxLength) {
      showMaxLengthExceededMsg();

      return;
    }

    // onEditorChange is the single gate on props.onChange: insertContent fires it, and it also
    // refreshes the remaining-characters count.
    editor.undoManager.transact(() => {
      editor.insertContent(html);
    });
    editor.nodeChanged();
    closeReferencePicker();
  };

  //
  // Plugin & Toolbar setup
  //
  const getPlugins = (): string[] => {
    let plugins = [
      'autoresize',
      'emoticons',
      'advlist',
      'autolink',
      'lists',
      'link',
      'anchor',
      'searchreplace',
      'visualblocks',
      'code',
      'table',
      'quickbars',
      'tinymcespellchecker',
    ];

    if (props.onImageUpload) {
      plugins = [...plugins, 'image'];
    }

    if (!props.disallowFullscreen) {
      plugins = ['fullscreen', ...plugins];
    }

    return plugins;
  };

  const getToolbar = (): string => {
    let toolbar: string = '';
    const linkGroup = [
      'link unlink',
      ...(canAuthorReferences ? ['isoreference'] : []),
      ...(props.onImageUpload ? ['image'] : []),
    ].join(' ');

    if (props.compactToolbar) {
      toolbar = `undo | styles bold underline | backcolor removeformat | bullist numlist | ${linkGroup}`;
    } else {
      toolbar = `undo redo | styles fontsize lineheight | bold italic underline strikethrough | forecolor backcolor removeformat | table bullist numlist emoticons | ${linkGroup} | outdent indent | alignleft aligncenter alignright alignjustify | spellchecker print code | help`;
    }
    if (!props.disallowFullscreen) {
      toolbar = 'fullscreen | ' + toolbar;
    }
    if (props.onAccept) {
      toolbar = 'customSaveButton customCancelButton | ' + toolbar;
    }

    return toolbar;
  };

  const getLangCode = (code: string): string | undefined => {
    //https://www.tiny.cloud/docs/tinymce/latest/introduction-to-tiny-spellchecker/
    const supportedLangs = ['af', 'en', 'da', 'de', 'es', 'fi', 'fr', 'hu', 'it', 'nb', 'nl', 'pl', 'pt', 'sv'];

    //the user can change the language on the toolbar and this is remembered in local storage by TinyMCE
    if (supportedLangs.includes(code.toLowerCase())) {
      return code;
    } else {
      return 'en';
    }
  };

  const showAiPasteCleanedMsg = () => {
    appContext.showNotification(t('translation:RichText.AiPasteCleaned'), false, false, true);
  };

  //
  // Image upload: TinyMCE hands over every inserted, pasted or dropped image and waits for the URL
  //
  const getImageUploadInit = () => {
    const initialUpload = props.onImageUpload;
    if (!initialUpload) return {};

    return {
      automatic_uploads: true,
      images_file_types: getImageFileTypes(props.acceptedImageTypes ?? allowedWebImageMimeTypes),
      image_description: true,
      images_upload_handler: async (blobInfo: { blob: () => Blob; filename: () => string }): Promise<string> => {
        try {
          //TinyMCE reads the init once, when it starts, so the handler goes through the ref to reach the
          //callback of the latest render; the callback the editor started with stands in when it is unset
          const upload = onImageUploadRef.current ?? initialUpload;

          return await upload(blobInfo.blob(), blobInfo.filename());
        } catch (err) {
          //TinyMCE shows the message itself and, with remove, takes the failed image out of the content.
          //For an API error, `message` holds the response body; the readable text is in `data.message`.
          const appError = AppError.fromApiError(err);

          return Promise.reject({ message: appError.data?.message ?? appError.message, remove: true });
        }
      },
    };
  };

  //
  // Main render
  //
  return (
    <>
      <IPStack className={props.className}>
        <Editor
          // We also eager-load TinyMCE in index.html; the channel must match its script path
          apiKey="ly09v4a3kahz4m9653scj9iqospefqevznn1shd60x0ze6mg"
          cloudChannel="8"
          //
          // Set content
          //
          value={props.html}
          //
          // Event handlers
          //
          onInit={(evt, editor) => {
            editorRef.current = editor;

            //We need to set the onBlur event handler here because of a TinyMCE issue
            //At an onBlur event, the editor can be destroyed because of a closing panel/modal
            //If we use the onBlur prop of the Editor component, it will be triggered after the editor is destroyed, ]
            //causing a crash, especially on FireFox.
            //https://github.com/tinymce/tinymce/issues/5330
            editor.on('blur', function () {
              if (props.onBlur && !referenceOpen.current) {
                let lastValue = editorRef.current?.getContent();
                if (getLength(lastValue ?? '') > maxLength) {
                  // Only warn when the user changed something: content that already exceeded maxLength
                  // when it was loaded (TinyMCE serialization can grow it past the limit) must blur silently.
                  if (lastValue !== lastAcceptedContentRef.current) {
                    showMaxLengthExceededMsg();
                  }
                  lastValue = lastAcceptedContentRef.current;
                } else {
                  lastAcceptedContentRef.current = lastValue;
                }
                props.onBlur(lastValue, lastValue !== initialContentRef.current);
              }
            });

            if (props.onSelectionChange) {
              editor.on('SelectionChange', function () {
                props.onSelectionChange?.(editor.selection.getContent({ format: 'text' }));
              });
            }

            if (!props.disableAutoFocus) {
              editor.focus();
            }

            props.onInit?.();
          }}
          onKeyUp={(evt) => {
            if (evt.ctrlKey === false && evt.shiftKey === false && evt.altKey === false && evt.key === 'Enter') {
              // Remove format on Enter to get out of a badge
              editorRef.current?.editorCommands.execCommand('RemoveFormat');
            }
            if (props.onKeyUp) {
              const ev: Partial<React.KeyboardEvent> = {
                key: evt.key,
                ctrlKey: evt.ctrlKey,
                shiftKey: evt.shiftKey,
                altKey: evt.altKey,
                metaKey: evt.metaKey,
                preventDefault: evt.preventDefault,
                stopPropagation: evt.stopPropagation,
              };
              props.onKeyUp(ev);
            }
          }}
          onKeyDown={(evt) => {
            if (evt.ctrlKey === true && evt.key === 'Enter') {
              //ignore ctrl+enter: this key combination is used to submit forms
              evt.preventDefault();
            }
            if (props.onKeyDown) {
              const ev: Partial<React.KeyboardEvent> = {
                key: evt.key,
                ctrlKey: evt.ctrlKey,
                shiftKey: evt.shiftKey,
                altKey: evt.altKey,
                metaKey: evt.metaKey,
                preventDefault: evt.preventDefault,
                stopPropagation: evt.stopPropagation,
              };
              props.onKeyDown(ev);
            }
          }}
          onFocus={props.onFocus}
          onBeforeAddUndo={(evt, editor) => {
            //the same unit as the gate in onEditorChange: the html that is stored, not the visible text
            if (getLength(editor.getContent()) > maxLength) {
              evt.preventDefault();
            }
          }}
          onEditorChange={(value: string) => emitChange(value)}
          disabled={props.readOnly}
          //
          // Main init
          //
          init={{
            //auto_focus: props.disableAutoFocus === true ? 'false' : true,
            placeholder: props.placeholder,
            branding: false,
            toolbar_location: props.toolbarOnTop ? 'top' : 'bottom',
            skin: appContext.useDarkMode ? 'oxide-dark' : 'oxide',
            content_css: appContext.useDarkMode ? 'dark' : 'default',
            content_style:
              'body { line-height: 1.1; overflow-wrap: anywhere; word-break: break-word; } ' +
              globalOutcomeListCss(appContext.useDarkMode),
            language: appContext.user.language.code,
            spellchecker_language: getLangCode(appContext.user.language.code),
            menubar: false,
            statusbar: false,
            toolbar_mode: 'sliding',
            plugins: getPlugins(),
            // Only the plugins getPlugins() actually loads: naming a menu here replaces TinyMCE's
            // default, and dropping spellchecker would take the right-click spelling suggestions with it.
            contextmenu: 'isoreference link table spellchecker',
            paste_data_images: true,
            //only added when needed, so the init of the other editors is unchanged
            ...((props.cleanAiPaste || props.onPasteList) && {
              paste_preprocess: (_editor: TinyMCEEditor, args: { content: string; internal: boolean }) => {
                //content copied inside the editor itself is left as it is
                const cleaner = pasteCleanerRef.current;
                if (args.internal || !cleaner) return;

                if (onPasteListRef.current) {
                  const items = cleaner.splitPastedList(cleaner.pastedHtmlToPlainText(args.content) ?? '');
                  if (items && items.length >= 2) {
                    onPasteListRef.current(items, args.content);
                    args.content = '';

                    return;
                  }
                }
                if (props.cleanAiPaste) {
                  const result = cleaner.cleanAiPastedContent(args.content);
                  args.content = result.html;
                  if (result.changed) showAiPasteCleanedMsg();
                }
              },
            }),
            link_default_target: '_blank',
            toolbar: getToolbar(),
            font_size_formats: '8pt 9pt 10pt 11pt 12pt 14pt 18pt 24pt 30pt 36pt',
            height: props.height,
            max_height: props.maxHeight === -1 ? undefined : props.maxHeight,
            //opt-in options are added only when set, so the init of the other editors is unchanged
            ...(props.minHeight !== undefined && { min_height: props.minHeight }),
            //split `ui_mode` docks the toolbar in the nearest scroller instead of the window
            ...(props.stickyToolbar && { toolbar_sticky: true, ui_mode: 'split' as const }),
            style_formats_autohide: true,
            relative_urls: false,
            remove_script_host: false,
            //
            // Capture the loaded content as the baseline for change detection. This runs after tinymce-react has
            // re-applied the value prop (it resets the content when TinyMCE's serialization differs from the
            // value it was given), so the baseline is the final content, not an intermediate one.
            //
            init_instance_callback: (editor) => {
              initialContentRef.current = editor.getContent();
              // Same serialization form for the blur fallback, so an untouched blur where TinyMCE's
              // serialization exceeds maxLength falls back to the baseline and reports hasChanged false.
              lastAcceptedContentRef.current = initialContentRef.current;
            },
            //
            // Setup custom toolbar buttons
            //
            setup: (editor) => {
              if (canAuthorReferences) {
                editor.ui.registry.addButton('isoreference', {
                  icon: 'bookmark',
                  tooltip: t('translation:RichText.Reference.Insert'),
                  onAction: () => openReferencePicker(editor),
                });
              }
              const openSelectedReference = (anchor: HTMLAnchorElement) => {
                const id = parseISOControlReference(anchor.getAttribute('href'));
                if (id) window.open(getISOControlReferenceUrl(appContext, id), '_blank', 'noopener,noreferrer');
              };
              editor.ui.registry.addMenuItem('openisoreference', {
                text: t('translation:RichText.Reference.Open'),
                onAction: () => {
                  const anchor = editor.dom.getParent(editor.selection.getNode(), 'a') as HTMLAnchorElement | null;
                  if (anchor) openSelectedReference(anchor);
                },
              });
              editor.ui.registry.addContextMenu('isoreference', {
                update: (element) => {
                  const anchor = editor.dom.getParent(element, 'a') as HTMLAnchorElement | null;

                  return parseISOControlReference(anchor?.getAttribute('href')) ? 'openisoreference' : '';
                },
              });
              editor.on('click', (event) => {
                const anchor = editor.dom.getParent(event.target as Node, 'a') as HTMLAnchorElement | null;
                if (
                  anchor &&
                  parseISOControlReference(anchor.getAttribute('href')) &&
                  (event.ctrlKey || event.metaKey)
                ) {
                  event.preventDefault();
                  event.stopImmediatePropagation();
                  openSelectedReference(anchor);
                }
              });
              if (props.onAccept) {
                editor.ui.registry.addButton('customSaveButton', {
                  text: appContext.isMobileView ? '' : t('translation:General.Button.Update'),
                  icon: 'checkmark',
                  tooltip: t('translation:General.Button.Update'),
                  onAction: (_) => {
                    if (getLength(editor.getContent()) <= maxLength) {
                      if (props.onAccept) props.onAccept(editor.getContent(), true);
                    } else {
                      showMaxLengthExceededMsg();
                    }
                  },
                });
                editor.ui.registry.addButton('customCancelButton', {
                  text: appContext.isMobileView ? '' : t('translation:General.Button.Discard'),
                  icon: 'cancel',
                  tooltip: t('translation:General.Button.Discard'),
                  onAction: (_) => {
                    if (props.onAccept) props.onAccept(editor.getContent(), false);
                  },
                });
              }
              props.onSetup?.(editor);
            },
            //
            // Custom formats
            //
            style_formats: [
              { title: 'Paragraph', format: 'p' },
              { title: t('translation:RichText.OutcomeList'), selector: 'ul', classes: 'ip-outcome-list' },
              {
                title: 'Badge',
                inline: 'span',
                styles: {
                  display: 'inline-block',
                  border: '1px solid #2276d2',
                  'border-radius': '5px',
                  padding: '2px 5px',
                  margin: '0 2px',
                  color: '#2276d2',
                  cursor: 'pointer',
                },
              },
              {
                title: 'Info',
                inline: 'span',
                styles: {
                  display: 'inline-block',
                  background: '#cce5ff',
                  'border-radius': '5px',
                  padding: '5px 5px 5px 5px',
                  margin: '0 2px',
                },
              },
              {
                title: 'Note',
                inline: 'span',
                styles: {
                  display: 'inline-block',
                  background: '#ffffcc',
                  'border-radius': '5px',
                  padding: '5px 5px 5px 5px',
                  margin: '0 2px',
                },
              },
              {
                title: 'Success',
                inline: 'span',
                styles: {
                  display: 'inline-block',
                  background: '#ccffe6',
                  'border-radius': '5px',
                  padding: '5px 5px 5px 5px',
                  margin: '0 2px',
                },
              },
              {
                title: 'Warning',
                inline: 'span',
                styles: {
                  display: 'inline-block',
                  background: '#ffb3b3',
                  'border-radius': '5px',
                  padding: '5px 5px 5px 5px',
                  margin: '0 2px',
                },
              },
              { title: 'Header 1', format: 'h1' },
              { title: 'Header 2', format: 'h2' },
              { title: 'Header 3', format: 'h3' },
              { title: 'Header 4', format: 'h4' },
              { title: 'Header 5', format: 'h5' },
              { title: 'Blockquote', format: 'blockquote' },
              { title: 'Code', format: 'code' },
            ],
            ...getImageUploadInit(),
            quickbars_selection_toolbar: 'bold italic underline removeformat | link',
            quickbars_insert_toolbar: false,
            quickbars_image_toolbar: false,
          }}
        />
        {!props.hideRemainingChars && (
          <IPText variant="small">
            {t('translation:General.Text.RichTextEditorMaxLength', { len: maxLength - currentLength })}
          </IPText>
        )}
      </IPStack>
      {reference && (
        <ISOControlReferencePicker
          initialId={reference.id}
          initialLabel={reference.label}
          onClose={closeReferencePicker}
          onInsert={insertReference}
        />
      )}
    </>
  );
});

export default RichTextEditorMCE;
